import {UserStore} from "stores/userStore";
import type {IUserSettings} from "types";
import type {IUploadFileResponse} from "requests";
import type {ScanModeFlags} from "./scanParams";
import type {RanBatchDraft} from "stores/batchFileStore";
import type {ActiveSection} from "stores/inputStore";
import {safeRandomUUID} from "./lib/cryptoFallback";

const USER_CACHE_KEY = 'IA__user_cache';
const TOKEN_KEY = 'token';

/** Read the persisted auth token from localStorage. Null if none. */
export const getStoredToken = (): string | null => localStorage.getItem(TOKEN_KEY);

/** Persist an auth token obtained outside the store (see adminUtil's link login). */
export const setStoredToken = (token: string): void => localStorage.setItem(TOKEN_KEY, token);

/** Get or create a unique anonymous token stored in localStorage. */
export function getUniqueToken(): string {
    let uuid = localStorage.getItem('uuid');
    if (!uuid) {
        uuid = safeRandomUUID();
        localStorage.setItem('uuid', uuid);
    }
    return uuid;
}

/** Persist auth-related data to localStorage (only for main app, not widget). */
export const persistAuthData = (userStore: UserStore) => {
    if (userStore.token) {
        localStorage.setItem(TOKEN_KEY, userStore.token);
    }
    const userData = userStore.user;
    if (userData) {
        localStorage.setItem(USER_CACHE_KEY, JSON.stringify(userData));
    }
    if (userData?.uuid) {
        localStorage.setItem('IA__uuid', userData.uuid);
    }
    if (userData?.username) {
        localStorage.setItem('itsai_user_id', userData.username);
        localStorage.setItem('id', userData.username);
    }
}

/** Read a key from localStorage, tolerating storage that throws on access (blocked or
 *  partitioned third-party context) rather than taking the caller down with it. */
export const getStored = (key: string, storage?: Storage): string | null => {
    try {
        return (storage ?? localStorage).getItem(key);
    } catch {
        return null;
    }
}

/** Parse a JSON string that came out of localStorage. Null for anything unreadable — a
 *  half-written or outdated entry must never break the page it was meant to restore. */
export const parseStored = <T>(raw: string | null): T | null => {
    if (!raw) return null;
    try {
        return JSON.parse(raw) as T;
    } catch {
        return null;
    }
}

/** Stash a value under `key` with the moment it was written, so a later read can decide it
 *  is too old to act on. For the handful of short-lived hand-offs that survive a redirect —
 *  the pending guest checkout, the action a login was started for — where acting on a stale
 *  one is worse than losing it. */
export const writeExpiring = <T>(key: string, value: T, storage?: Storage): void => {
    try {
        (storage ?? localStorage).setItem(key, JSON.stringify({value, ts: Date.now()}));
    } catch {}
}

/** The value written by `writeExpiring`, or null if it is missing, unreadable or older than
 *  `ttlMs`. */
export const readExpiring = <T>(key: string, ttlMs: number, storage?: Storage): T | null => {
    const stored = parseStored<{value: T; ts: number}>(getStored(key, storage));
    if (!stored || Date.now() - stored.ts > ttlMs) return null;
    return stored.value;
}

/** Restore cached user settings from localStorage for instant initial paint. */
export const getCachedUser = (): IUserSettings | null =>
    parseStored<IUserSettings>(getStored(USER_CACHE_KEY));

/** Clear persisted auth data from localStorage on logout. */
export const clearPersistedAuth = () => {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem('username');
    localStorage.removeItem('id');
    localStorage.removeItem('itsai_user_id');
    localStorage.removeItem(USER_CACHE_KEY);
    // The parked page is the account's text and analysis — the next account must not get it.
    localStorage.removeItem(CHECKOUT_PAGE_KEY);
}

/** A "show this once per browser, ever" flag. */
const oneShotFlag = (key: string) => ({
    seen: (): boolean => getStored(key) === 'true',
    mark: (): void => { try { localStorage.setItem(key, 'true'); } catch {} },
});

const paywallFirstClose = oneShotFlag('IA__paywall_first_close_done');
export const hasSeenPaywallFirstClose = paywallFirstClose.seen;
export const markPaywallFirstClose = paywallFirstClose.mark;

// The Humanizer output's composition popover, opened on its own after the first run.
const humanizerResultTip = oneShotFlag('IA__humanizer_result_tip_done');
export const hasSeenHumanizerResultTip = humanizerResultTip.seen;
export const markHumanizerResultTip = humanizerResultTip.mark;

// The "!" tooltip beside Scan once, opened on its own the first time the plan refuses a run.
const planBlockTip = oneShotFlag('IA__plan_block_tip_done');
export const hasSeenPlanBlockTip = planBlockTip.seen;
export const markPlanBlockTip = planBlockTip.mark;

// The hint over Extra tools that comes up with that "!".
const oneTimeToolsHint = oneShotFlag('IA__one_time_tools_hint_done');
export const hasSeenOneTimeToolsHint = oneTimeToolsHint.seen;
export const markOneTimeToolsHint = oneTimeToolsHint.mark;

// The batch's "Select multiple tools" hint over its Extra tools. The key predates the IA__
// naming and stays, so browsers that already saw the hint keep it seen.
const batchToolsHint = oneShotFlag('batchToolsHintSeen');
export const hasSeenBatchToolsHint = batchToolsHint.seen;
export const markBatchToolsHint = batchToolsHint.mark;

// "Replace current text?" answered with "Don't ask again": View Analysis and Restore replace
// the scan tab's text from then on, without asking.
const replaceTextSilenced = oneShotFlag('IA__humanizer_replace_silenced');
export const hasSilencedReplaceText = replaceTextSilenced.seen;
export const markReplaceTextSilenced = replaceTextSilenced.mark;

// The trial-ending notice, closed for one trial (keyed by its end) — it comes back for
// the next trial and not before.
const TRIAL_NOTICE_DISMISSED_KEY = 'IA__trial_notice_dismissed';
export const trialNoticeDismissedFor = (): string | null => getStored(TRIAL_NOTICE_DISMISSED_KEY);
export const markTrialNoticeDismissed = (trialEnd: string): void => {
    try { localStorage.setItem(TRIAL_NOTICE_DISMISSED_KEY, trialEnd); } catch {}
};

// The widget is served from the app's origin, so it shares the app's localStorage: its login
// drafts are kept to its own tab, or one parked for the host's login lands in the app opened
// next to it (init restores drafts on every load).
let draftsInTab = false;
/** Keep the login drafts (and the post-login action) in this tab's sessionStorage — the widget's. */
export const keepDraftsInTab = (on: boolean) => { draftsInTab = on; };
export const draftStorage = (): Storage => draftsInTab ? sessionStorage : localStorage;

// A persisted draft is ignored once it's older than this — a stale reload
// shouldn't silently replay what the user typed minutes ago.
const DRAFT_TTL_MS = 1000 * 60 * 5;

// The Humanizer's text and mode ride localStorage across a login the same way
// the scan drafts below do (OAuth reloads the page). Written by the login
// itself, never by the Humanize click: `autoRun` is true only when the login
// was raised BY that click, so a prompt the user closed cannot replay a
// charged run on a later, unrelated login.
const HUMANIZER_DRAFT_KEY = 'IA__humanizer_draft';

export interface HumanizerDraft {
    text: string;
    mode: string;
    autoRun: boolean;
}

export const persistHumanizerDraft = (text: string, mode: string, autoRun: boolean): void =>
    writeExpiring<HumanizerDraft>(HUMANIZER_DRAFT_KEY, {text, mode, autoRun}, draftStorage());

export const clearHumanizerDraft = (): void => draftStorage().removeItem(HUMANIZER_DRAFT_KEY);

/** Read AND clear the draft — a draft is for one login, not for every reload after it. */
export const consumeHumanizerDraft = (): HumanizerDraft | null => {
    const draft = readExpiring<HumanizerDraft>(HUMANIZER_DRAFT_KEY, DRAFT_TTL_MS, draftStorage());
    clearHumanizerDraft();
    return draft?.text ? draft : null;
};

// A batch that ran is only in memory: OAuth reloads the page, so the login carries the
// files with the analyses that hold their results, and the restore fetches them back.
const RAN_BATCH_DRAFT_KEY = "persist-ran-batch";

/** Like the other drafts, rewritten by every login: one with no ran batch clears it. */
export const persistRanBatchDraft = (draft: RanBatchDraft): void => {
    if (draft.files.length) writeExpiring<RanBatchDraft>(RAN_BATCH_DRAFT_KEY, draft, draftStorage());
    else draftStorage().removeItem(RAN_BATCH_DRAFT_KEY);
};

/** Read AND clear the draft — a draft is for one login, not for every reload after it. */
export const consumeRanBatchDraft = (): RanBatchDraft | null => {
    const draft = readExpiring<RanBatchDraft>(RAN_BATCH_DRAFT_KEY, DRAFT_TTL_MS, draftStorage());
    draftStorage().removeItem(RAN_BATCH_DRAFT_KEY);
    return draft?.files?.length ? {...draft, modes: toModes(draft.modes ?? {})} : null;
};

// `autoRun` records why the login was started: true when it was triggered to
// continue a scan the user had already asked for (the limit auth form), false for
// a voluntary "log in" click. The restore paths always re-hydrate the draft to
// preserve state, but only re-run the scan when autoRun is set.

const isDraftStale = (ts: string | null): boolean =>
    ts !== null && Date.now() - Number(ts) > DRAFT_TTL_MS;

export interface InputDraft {
    text: string;
    /** null when the stored selection can't be read — the caller then keeps the current flags. */
    modes: ScanModeFlags | null;
    autoRun: boolean;
    /** The analysis whose result was on screen for this text: the login that unlocks it
     *  brings it back instead of a re-run. */
    analysis: string | null;
}

/** Save input draft to localStorage before login redirect. The whole scan-mode selection
 *  travels with it: the login round-trip returns to a callback URL that no longer carries
 *  the scan params, so anything not in the draft is lost (grammar and plagiarism were). */
export const persistInputDraft = (text: string, modes: ScanModeFlags, autoRun: boolean, analysis: string | null) => {
    draftStorage().setItem("persist-text", text);
    draftStorage().setItem("persist-modes", JSON.stringify(modes));
    draftStorage().setItem("persist-autorun", autoRun.toString());
    if (analysis) draftStorage().setItem("persist-analysis", analysis);
    else draftStorage().removeItem("persist-analysis");
    draftStorage().setItem("persist-ts", Date.now().toString());
}

/** Restore input draft from localStorage (if saved less than 5 min ago). */
export const restoreInputDraft = (): InputDraft | null => {
    const text = draftStorage().getItem("persist-text");
    const rawModes = draftStorage().getItem("persist-modes");
    const autoRun = draftStorage().getItem("persist-autorun") === "true";
    const analysis = draftStorage().getItem("persist-analysis");
    const ts = draftStorage().getItem("persist-ts");
    draftStorage().removeItem("persist-text");
    draftStorage().removeItem("persist-modes");
    draftStorage().removeItem("persist-deep-scan"); // the key this replaced — clear it once
    draftStorage().removeItem("persist-autorun");
    draftStorage().removeItem("persist-analysis");
    draftStorage().removeItem("persist-ts");
    if (!text || isDraftStale(ts)) return null;
    return { text, modes: parseModes(rawModes), autoRun, analysis };
}

const toModes = (m: Partial<ScanModeFlags>): ScanModeFlags =>
    ({ai: !!m.ai, deep: !!m.deep, plagiarism: !!m.plagiarism, grammar: !!m.grammar});

const parseModes = (raw: string | null): ScanModeFlags | null => {
    const m = parseStored<ScanModeFlags>(raw);
    return m && toModes(m);
}

/** Forget the files draft — the four keys are one record, written and dropped together. */
const clearFilesDraft = () => {
    draftStorage().removeItem("persist-files");
    draftStorage().removeItem("persist-files-modes");
    draftStorage().removeItem("persist-files-autorun");
    draftStorage().removeItem("persist-files-ts");
}

/** Save uploaded-file state to localStorage before a login redirect. We persist only
 *  server-settled metadata (uuid, sizes, counts) — NOT the File bytes, which can't
 *  survive a reload; the scan runs by uuid and restore re-creates a placeholder File
 *  (see batchFileStore.injectUploadedFiles). Mirrors persistInputDraft for files, modes
 *  included: a batch is restored — and re-run — in the modes it was staged with.
 *
 *  A login that stages nothing says so: it CLEARS the draft rather than leaving the
 *  previous one behind. Otherwise a batch whose login form the user closed outlives it and
 *  is restored by the next login — landing beside a scan that has finished since, and
 *  re-running itself, because autoRun travels with the draft. */
export const persistFilesDraft = (files: IUploadFileResponse[], modes: ScanModeFlags, autoRun: boolean) => {
    if (!files.length) return clearFilesDraft();
    draftStorage().setItem("persist-files", JSON.stringify(files));
    draftStorage().setItem("persist-files-modes", JSON.stringify(modes));
    draftStorage().setItem("persist-files-autorun", autoRun.toString());
    draftStorage().setItem("persist-files-ts", Date.now().toString());
}

/** Restore uploaded-file state from localStorage (if saved less than 5 min ago).
 *  One-shot: the keys are cleared on read, like restoreInputDraft. */
export const restoreFilesDraft = (): { files: IUploadFileResponse[]; modes: ScanModeFlags | null; autoRun: boolean } | null => {
    const raw = draftStorage().getItem("persist-files");
    const rawModes = draftStorage().getItem("persist-files-modes");
    const autoRun = draftStorage().getItem("persist-files-autorun") === "true";
    const ts = draftStorage().getItem("persist-files-ts");
    clearFilesDraft();
    if (isDraftStale(ts)) return null;
    const files = parseStored<IUploadFileResponse[]>(raw);
    return Array.isArray(files) ? { files, modes: parseModes(rawModes), autoRun } : null;
}

/** The scan page a paywall's hosted Stripe Checkout took the buyer away from — what the login
 *  drafts above carry, in one record. `humanizer` is set when the Humanizer was the open folder. */
export interface CheckoutPageDraft {
    section: ActiveSection;
    text: string;
    modes: ScanModeFlags;
    analysis: string | null;
    files: IUploadFileResponse[];
    filesModes: ScanModeFlags;
    ranBatch: RanBatchDraft;
    humanizer: {text: string; mode: string} | null;
}

// Its own key, not the login drafts': init restores those on every load, the plans page the
// checkout returns to included, and only for the five minutes a login round-trip takes.
const CHECKOUT_PAGE_KEY = "persist-checkout-page";
// Typing a card and passing 3DS on Stripe's page takes longer than a login round-trip.
const CHECKOUT_PAGE_TTL_MS = 1000 * 60 * 30;

/** Park the page, or with nothing to park drop an earlier one: a checkout abandoned with Back
 *  must not hand its page to the next, unrelated one. */
export const persistCheckoutPage = (draft: CheckoutPageDraft | null) => writeExpiring(CHECKOUT_PAGE_KEY, draft);

/** A page is parked — the plans page sends the buyer on to the scan page that restores it. */
export const hasCheckoutPage = (): boolean => !!readExpiring<CheckoutPageDraft>(CHECKOUT_PAGE_KEY, CHECKOUT_PAGE_TTL_MS);

/** One-shot, like the login drafts. */
export const consumeCheckoutPage = (): CheckoutPageDraft | null => {
    const draft = readExpiring<CheckoutPageDraft>(CHECKOUT_PAGE_KEY, CHECKOUT_PAGE_TTL_MS);
    localStorage.removeItem(CHECKOUT_PAGE_KEY);
    return draft && {
        ...draft,
        modes: toModes(draft.modes ?? {}),
        filesModes: toModes(draft.filesModes ?? {}),
        ranBatch: {...draft.ranBatch, modes: toModes(draft.ranBatch?.modes ?? {})},
    };
}
